import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { GBP, ukDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Gift, Layers, AlertTriangle, Plus, Trash2 } from "lucide-react";

function DeleteVoucher({ v, onDeleted }) {
  const del = async () => {
    const paid = v.source === "online" ? "\n\nThis voucher was bought online. Deleting it does not refund the buyer." : "";
    if (!window.confirm(`Delete voucher ${v.code}? The code will stop working.${paid}`)) return;
    try { await api.delete(`/vouchers/${v.id}`); toast.success(`Voucher ${v.code} deleted`); onDeleted(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return <Button size="sm" variant="ghost" aria-label="Delete voucher" className="rounded-full text-rose-600" data-testid={`voucher-delete-${v.code}`} onClick={del}><Trash2 className="h-4 w-4" /></Button>;
}

function VoucherRow({ v, onRedeem, onDeleted }) {
  const [amt, setAmt] = useState("");
  return (
    <div data-testid={`voucher-${v.code}`} className="bg-white rounded-2xl border border-stone-200 p-4 shadow-sm flex flex-wrap items-center justify-between gap-3">
      <div>
        <div className="font-mono font-semibold">{v.code} <span className={`ml-2 text-[11px] font-sans px-2 py-0.5 rounded-full ${v.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-stone-100 text-stone-500"}`}>{v.status}</span></div>
        <div className="text-sm text-stone-600">{v.service_name ? <b className="text-stone-800">{v.service_name} · </b> : null}For {v.recipient_name || "—"} · from {v.purchaser_name} · {ukDate(v.created_at)} · {v.source === "online" ? "Bought online" : "Issued in salon"}</div>
      </div>
      <div className="flex items-center gap-3">
        <div className="text-right"><div className="text-xs text-stone-500">{v.kind === "service" ? "Treatment" : "Balance"}</div><div className="font-mono font-bold">{v.kind === "service" ? GBP(v.amount) : <>{GBP(v.balance)} <span className="text-stone-400 font-normal text-xs">/ {GBP(v.amount)}</span></>}</div></div>
        {v.status === "active" && v.kind === "service" && (
          <Button size="sm" className="rounded-full bg-stone-900" data-testid={`voucher-redeem-${v.code}`} onClick={() => onRedeem(v.code, null)}>Mark used</Button>
        )}
        {v.status === "active" && v.kind !== "service" && (<>
          <Input type="number" placeholder="£" value={amt} onChange={(e) => setAmt(e.target.value)} className="w-20 h-9 rounded-lg" data-testid={`voucher-redeem-amount-${v.code}`} />
          <Button size="sm" className="rounded-full bg-stone-900" data-testid={`voucher-redeem-${v.code}`} disabled={!amt} onClick={() => { onRedeem(v.code, Number(amt)); setAmt(""); }}>Redeem</Button>
        </>)}
        <DeleteVoucher v={v} onDeleted={onDeleted} />
      </div>
    </div>
  );
}

function NewVoucher({ onCreated, services }) {
  const [f, setF] = useState({ amount: "", recipient_name: "", service_id: "" });
  const [mode, setMode] = useState("amount");
  const giftable = services.filter((s) => s.is_active && !s.is_addon && s.price_type !== "from" && s.price > 0);
  const create = async () => {
    const body = mode === "service" ? { service_id: f.service_id, recipient_name: f.recipient_name } : { amount: Number(f.amount), recipient_name: f.recipient_name };
    try { const { data } = await api.post("/vouchers", body); toast.success(`Voucher ${data.code} created`); setF({ amount: "", recipient_name: "", service_id: "" }); onCreated(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <div className="bg-white rounded-2xl border border-stone-200 p-4 shadow-sm flex flex-wrap items-end gap-3">
      <div><Label>Voucher for</Label>
        <select data-testid="new-voucher-mode" value={mode} onChange={(e) => setMode(e.target.value)} className="mt-1.5 block h-10 rounded-lg border border-stone-300 px-2 text-sm bg-white"><option value="amount">Money amount</option><option value="service">A treatment</option></select>
      </div>
      {mode === "service" ? (
        <div className="min-w-[200px] flex-1"><Label>Treatment</Label>
          <select data-testid="new-voucher-service" value={f.service_id} onChange={(e) => setF({ ...f, service_id: e.target.value })} className="mt-1.5 block w-full h-10 rounded-lg border border-stone-300 px-2 text-sm bg-white">
            <option value="">Choose…</option>
            {[...new Set(giftable.map((s) => s.category))].map((c) => (
              <optgroup key={c} label={c}>{giftable.filter((s) => s.category === c).map((s) => <option key={s.id} value={s.id}>{s.name} — {GBP(s.price)}</option>)}</optgroup>
            ))}
          </select>
        </div>
      ) : (
        <div><Label>Amount (£)</Label><Input type="number" data-testid="new-voucher-amount" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className="mt-1.5 h-10 w-28 rounded-lg" /></div>
      )}
      <div className="flex-1 min-w-[160px]"><Label>Recipient name</Label><Input data-testid="new-voucher-recipient" value={f.recipient_name} onChange={(e) => setF({ ...f, recipient_name: e.target.value })} className="mt-1.5 h-10 rounded-lg" /></div>
      <Button data-testid="new-voucher-create" disabled={mode === "service" ? !f.service_id : !f.amount} onClick={create} className="rounded-full bg-stone-900"><Plus className="h-4 w-4 mr-1" />Issue voucher</Button>
    </div>
  );
}

export default function Sales() {
  const [vouchers, setVouchers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [services, setServices] = useState([]);
  const load = useCallback(async () => {
    const [v, c, s] = await Promise.all([api.get("/vouchers"), api.get("/courses"), api.get("/services")]);
    setVouchers(v.data); setCourses(c.data); setServices(s.data);
  }, []);
  useEffect(() => { load(); }, [load]);
  const redeem = async (code, amount) => {
    try { const { data } = await api.post(`/vouchers/${code}/redeem`, amount ? { amount } : {}); toast.success(amount ? `Redeemed. Balance ${GBP(data.balance)}` : "Voucher marked as used"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const warned = courses.filter((c) => c.warnings.length).length;

  return (
    <div className="max-w-4xl">
      <h1 className="font-extrabold text-3xl">Vouchers & courses</h1>
      <p className="text-stone-600 text-sm mt-1">Gift vouchers, e-vouchers and prepaid treatment courses</p>
      <Tabs defaultValue="vouchers" className="mt-5">
        <TabsList className="rounded-full">
          <TabsTrigger value="vouchers" data-testid="sales-tab-vouchers" className="rounded-full"><Gift className="h-3.5 w-3.5 mr-1" />Vouchers ({vouchers.length})</TabsTrigger>
          <TabsTrigger value="courses" data-testid="sales-tab-courses" className="rounded-full"><Layers className="h-3.5 w-3.5 mr-1" />Courses ({courses.length}){warned > 0 && <span className="ml-1 text-amber-600">· {warned} ⚠</span>}</TabsTrigger>
        </TabsList>
        <TabsContent value="vouchers" className="mt-5 space-y-3">
          <NewVoucher onCreated={load} services={services} />
          {vouchers.map((v) => <VoucherRow key={v.id} v={v} onRedeem={redeem} onDeleted={load} />)}
          {vouchers.length === 0 && <p className="text-sm text-stone-500">No vouchers yet. Clients can buy e-vouchers from your booking page.</p>}
        </TabsContent>
        <TabsContent value="courses" className="mt-5 space-y-3">
          {courses.length === 0 && <p className="text-sm text-stone-500">No courses sold yet.</p>}
          {courses.map((c) => (
            <div key={c.id} data-testid={`course-${c.id}`} className={`bg-white rounded-2xl border p-4 shadow-sm ${c.warnings.length ? "border-amber-300" : "border-stone-200"}`}>
              <div className="flex flex-wrap justify-between gap-2">
                <div><div className="font-semibold">{c.client_name} · {c.service_name}</div><div className="text-sm text-stone-600">{c.client_email} · {c.client_mobile} · bought {ukDate(c.created_at)}</div></div>
                <div className="text-right"><div className="font-mono font-bold">{c.sessions_used}/{c.sessions_total} sessions</div><div className="text-xs text-stone-500">Paid {GBP(c.price_paid)} ({c.percent}% off)</div></div>
              </div>
              {c.session_dates.length > 0 && <div className="text-xs text-stone-500 mt-2">Sessions: {c.session_dates.map(ukDate).join(", ")}</div>}
              {c.warnings.map((w) => <div key={w} data-testid={`course-warning-${c.id}`} className="mt-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5 flex items-center gap-2"><AlertTriangle className="h-4 w-4" />{w}</div>)}
            </div>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
